import { lireJson, type Resultat } from './client';

/** Compte connecté, tel que le serveur le décrit. */
export type Role = 'prestataire' | 'admin';

export interface Utilisateur {
  readonly id: string;
  readonly email: string;
  readonly nom: string;
  /** Le serveur décide des droits ; l'écran ne fait que cacher ce qui est interdit. */
  readonly role: Role;
  readonly actif: boolean;
  readonly creeLe: string;
}

/** Vrai si le compte connecté a le rôle administrateur. */
export function estAdmin(utilisateur: Utilisateur | null): boolean {
  return utilisateur?.role === 'admin';
}

interface ReponseSession {
  readonly utilisateur: Utilisateur;
  readonly expireDansSecs?: number;
}

/**
 * Envoie un corps JSON. Le jeton de session ne transite jamais par le corps :
 * il vit dans un cookie `HttpOnly`, que ce module ne lit donc jamais.
 */
async function poster<T>(
  chemin: string,
  corps: unknown,
  options: { fetchImpl?: typeof fetch } = {},
): Promise<Resultat<T>> {
  const { fetchImpl = fetch } = options;

  try {
    const reponse = await fetchImpl(chemin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corps),
      credentials: 'same-origin',
    });

    if (!reponse.ok) {
      let message = `Le serveur répond ${reponse.status}.`;
      try {
        const erreur = (await reponse.json()) as { error?: unknown };
        if (typeof erreur.error === 'string' && erreur.error.trim() !== '') {
          message = erreur.error;
        }
      } catch {
        /* corps non JSON : le statut suffit */
      }
      return { ok: false, message };
    }

    // 204 : pas de corps à décoder.
    if (reponse.status === 204) return { ok: true };

    return { ok: true, donnees: (await reponse.json()) as T };
  } catch {
    return {
      ok: false,
      message: 'Serveur injoignable — vérifiez qu’il tourne sur le port 3101.',
    };
  }
}

export function connecter(
  email: string,
  motDePasse: string,
  seSouvenir = false,
  options: { fetchImpl?: typeof fetch } = {},
): Promise<Resultat<ReponseSession>> {
  return poster<ReponseSession>(
    '/api/auth/connexion',
    { email, mot_de_passe: motDePasse, se_souvenir: seSouvenir },
    options,
  );
}

export function deconnecter(options: { fetchImpl?: typeof fetch } = {}): Promise<Resultat<void>> {
  return poster<void>('/api/auth/deconnexion', {}, options);
}

/** Qui est connecté ? 401 = personne. */
export async function lireSession(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Utilisateur | null> {
  const resultat = await lireJson<ReponseSession>('/api/auth/moi', options);
  return resultat.ok && resultat.donnees ? resultat.donnees.utilisateur : null;
}
